import React, { useEffect } from 'react';
import ActionButton from '@westwing/ui-kit/ActionButton';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import { body2, caption1 } from '@westwing/ui-kit/typography';
import styled, { css } from 'styled-components';
import { useDispatch } from 'react-redux';
import useTranslator from '../../../../hooks/useTranslator';
import Header from '../Header';
import { unblockUser, unblockUserActions } from 'Client/redux/user/unblock/actions';
import { OverlayId } from 'Client/components/Overlays/types';
import useUnblockOverlay from 'Client/hooks/useUnblockOverlay';
import useConfig from 'AppShell/hooks/useConfig';
import { BellaTestIds } from 'Common/qaIds';

const errorStyles = css`
    ${({ theme }) => `color: ${theme.colors.red90};`}
    ${({ theme }) => `border: 1px solid ${theme.colors.red90};`}
`;

const StyledWrapper = styled.div`
    margin: 0 30px 0 30px;
    text-align: left;
    ${forDesktop`
        margin: 0 50px 0 50px;
    `}
`;

const StyledBtnAccept = styled(ActionButton)`
    margin-top: 24px;
`;

const StyledBtnBack = styled(ActionButton)<{ $hasError: boolean }>`
    margin-top: 22px;
    ${({ $hasError }) =>
        $hasError &&
        css`
            ${errorStyles}
            &:focus {
                ${errorStyles}
            }
            &:hover {
                ${errorStyles}
            }
        `}
`;

const StyledText = styled.div`
    ${body2}
    text-align: center;
    margin-top: 22px;
`;

const StyledFooterText = styled.div`
    border-top: ${({ theme }) => `1px solid ${theme.colors.charcoal30}`};
    margin-top: 20px;
    padding-top: 12px;
    ${caption1}
    font-size: 10px;
    line-height: 16.8px;
    letter-spacing: 0.17;
`;

const StyledErrorMsgMobile = styled.span`
    ${caption1}
    color: ${({ theme }) => theme.colors.red90};
    ${forDesktop`
        display: none;
    `}
`;

const StyledErrorMsgDesktop = styled(StyledErrorMsgMobile)`
    display: none;
    ${forDesktop`
        display: inline;
    `}
`;

interface UnblockProps {
    rejectCount: number;
    setRejectCount: (rejectCount: number) => void;
    email: string;
    password: string;
    overlayId: OverlayId;
}

const Unblock = ({ rejectCount, setRejectCount, email, password, overlayId }: UnblockProps) => {
    const t = useTranslator();
    const dispatch = useDispatch();
    const title = t('datapolicy_header_bella');
    const {
        links: { dataProtection },
    } = useConfig();
    const {
        trackUnblockOverlayImpression,
        trackUnblockOverlayAccept,
        trackUnblockOverlayRejectFirstTime,
        trackUnblockOverlayRejectSecondTime,
    } = useUnblockOverlay();

    useEffect(() => {
        trackUnblockOverlayImpression();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAcceptClick = () => {
        dispatch(
            unblockUser({
                credentials: { email, password },
                overlayId,
            })
        );
        trackUnblockOverlayAccept();
    };

    const handleRejectClick = () => {
        const newRejectCount = rejectCount + 1;
        setRejectCount(newRejectCount);
        if (newRejectCount === 1) {
            trackUnblockOverlayRejectFirstTime();
        }
        if (newRejectCount === 2) {
            trackUnblockOverlayRejectSecondTime();
            dispatch(unblockUserActions.success());
        }
    };

    return (
        <StyledWrapper>
            <Header title={title} infoText="" />
            <StyledText
                dangerouslySetInnerHTML={{
                    __html: t('datapolicy_login_text_bella', [email]),
                }}
            />

            <StyledBtnAccept
                data-testid={BellaTestIds.unblockUserOverlayAcceptBtn}
                kind="primary"
                onClick={handleAcceptClick}
            >
                {t('datapolicy_accept_bella')}
            </StyledBtnAccept>
            <StyledBtnBack
                data-testid={BellaTestIds.unblockUserOverlayDenyBtn}
                kind="secondary"
                $hasError={rejectCount > 0}
                onClick={handleRejectClick}
            >
                {t('datapolicy_back_bella')}
            </StyledBtnBack>
            {rejectCount > 0 && (
                <>
                    <StyledErrorMsgMobile
                        data-testid={BellaTestIds.unblockUserOverlayDenyErrorMessage}
                        dangerouslySetInnerHTML={{
                            __html: t('datapolicy_are_you_sure_mobile_bella'),
                        }}
                    />
                    <StyledErrorMsgDesktop
                        data-testid={BellaTestIds.unblockUserOverlayDenyErrorMessage}
                        dangerouslySetInnerHTML={{
                            __html: t('datapolicy_are_you_sure_bella'),
                        }}
                    />
                </>
            )}
            <StyledFooterText
                dangerouslySetInnerHTML={{
                    __html: t('datapolicy_footnote_bella', [
                        `<a href="${dataProtection}" target="_blank">`,
                        '</a>',
                        `<a href="${dataProtection}" target="_blank">`,
                        '</a>',
                    ]),
                }}
            />
        </StyledWrapper>
    );
};

export default Unblock;
